// Package imports
import React, { useState } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

// CSS imports
import './TextEditor.css';

// Component imports
import TextEditorLink from '../textEditorLink/TextEditorLink';

// Helper imports
import OutsideAlerter from '../../../helpers/components/outsideAlerter/OutsideAlerter';
import { convertBase64 } from '../../../helpers/functions/convertBase64';

type Props = {
  className?: string;
  content: string;
  placeholder?: string;
  setContent: (x: string) => void;
  setContentError?: (x: string) => void;
};

const TextEditor: React.FC<Props> = ({
  className,
  content,
  placeholder,
  setContent,
  setContentError,
}: Props) => {
  const [showLink, setShowLink] = useState(false);
  const [linkText, setLinkText] = useState('');

  const extensions = [
    StarterKit,
    Image.configure({
      allowBase64: true,
    }),
    Underline,
    Link.configure({
      HTMLAttributes: {
        target: '__blank',
      },
    }),
    Placeholder.configure({
      placeholder: placeholder ? placeholder : 'Write your article ...',
    }),
  ];

  const onChange = (e: any) => {
    setContent(e.editor.getHTML());
    if (setContentError) {
      setContentError('');
    }
  };

  const handleDrop = (view: any, event: any, slice: any, moved: any) => {
    if (
      !moved &&
      event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files[0]
    ) {
      // if dropping external files
      let file = event.dataTransfer.files[0]; // the dropped file
      let filesize = (file.size / 1024 / 1024).toFixed(4); // get the filesize in MB
      if (
        (file.type === 'image/jpeg' ||
          file.type === 'image/png' ||
          file.type === 'image/webp') &&
        Number(filesize) < 10
      ) {
        convertBase64(file).then((base64: any) => {
          const { schema } = view.state;
          const coordinates = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          });
          const node = schema.nodes.image.create({
            src: base64,
            alt: file.name.replace(/[^a-zA-Z ]/g, '').replace(/ /g, ''),
          });
          const transaction = view.state.tr.insert(coordinates.pos, node); // places it in the correct position
          return view.dispatch(transaction);
        });
      } else {
        window.alert(
          'Images need to be in jpg or png format and less than 10mb in size.'
        );
      }
      return true; // handled
    }
    return false; // not handled use default behaviour
  };

  const editor = useEditor({
    extensions: extensions,
    content: content,
    editorProps: {
      handleDrop,
    },
    onUpdate: onChange,
  });

  const setBold = () => {
    editor!.chain().focus().toggleBold().run();
    setShowLink(false);
  };

  const setItalic = () => {
    editor!.chain().focus().toggleItalic().run();
    setShowLink(false);
  };

  const setUnderline = () => {
    editor!.commands.toggleUnderline();
    setShowLink(false);
  };

  const setHeading2 = () => {
    editor!.chain().focus().toggleHeading({ level: 2 }).run();
    setShowLink(false);
  };

  const setHeading3 = () => {
    editor!.chain().focus().toggleHeading({ level: 3 }).run();
    setShowLink(false);
  };

  const setBlockquote = () => {
    editor!.chain().focus().toggleBlockquote().run();
    setShowLink(false);
  };

  const setCode = () => {
    editor!.chain().focus().toggleCodeBlock().run();
    setShowLink(false);
  };

  const setLink = (newLink: string) => {
    editor!
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: newLink })
      .run();

    setShowLink(false);
  };

  const handleLink = () => {
    const previousUrl = editor!.getAttributes('link').href;
    if (previousUrl != null && previousUrl !== '') {
      setShowLink(true);
      setLinkText(previousUrl);
    } else if (!showLink) {
      setShowLink(true);
      setLinkText('');
    } else {
      setShowLink(false);
      setLinkText('');
    }
  };

  const closeLinkModal = () => {
    setShowLink(false);
    setLinkText('');
  };

  const removeLink = () => {
    setShowLink(false);
    editor!.chain().focus().unsetLink().run();
    setLinkText('');
  };

  return editor != null ? (
    <div className={`TextEditor ${className ? className : ''}`}>
      <EditorContent editor={editor} className='' />
      <BubbleMenu editor={editor}>
        <OutsideAlerter toggleEvent={closeLinkModal} dependency={showLink}>
          <div className='editor-menu'>
            <div className='editor-button-container'>
              <button
                onClick={setBold}
                className={editor.isActive('bold') ? 'is-active' : ''}
              >
                B
              </button>
              <button
                onClick={setItalic}
                className={`italic ${
                  editor.isActive('italic') ? 'is-active' : ''
                }`}
              >
                I
              </button>
              <button
                onClick={setUnderline}
                className={`underline ${
                  editor.isActive('underline') ? 'is-active' : ''
                }`}
              >
                U
              </button>
              <button
                onClick={setHeading2}
                className={`${
                  editor.isActive('heading', { level: 2 }) ? 'is-active' : ''
                }`}
              >
                H2
              </button>
              <button
                onClick={setHeading3}
                className={`${
                  editor.isActive('heading', { level: 3 }) ? 'is-active' : ''
                }`}
              >
                H3
              </button>
              <button
                onClick={setBlockquote}
                className={`${
                  editor.isActive('blockquote') ? 'is-active' : ''
                }`}
              >
                "
              </button>
              <button
                onClick={setCode}
                className={`${editor.isActive('codeBlock') ? 'is-active' : ''}`}
              >
                {'<>'}
              </button>
              <button
                onClick={handleLink}
                className={`${editor.isActive('link') ? 'is-active' : ''}`}
              >
                {'#'}
              </button>
            </div>
            {showLink ? (
              <TextEditorLink
                linkText={linkText}
                hasPreviousUrl={
                  editor.getAttributes('link').href != null &&
                  editor.getAttributes('link').href !== ''
                }
                setLinkText={setLinkText}
                setLink={setLink}
                removeLink={removeLink}
              />
            ) : null}
          </div>
        </OutsideAlerter>
      </BubbleMenu>
    </div>
  ) : null;
};

export default TextEditor;
